import { useAnalytics } from "@codat/analytics";
import {
  Button,
  Card,
  Skeleton,
  Typography,
  useToast,
} from "@codat/orchard-ui";
import { ClientImageUrls, useClientImageUrlsQuery } from "api/clientImages";
import {
  useAcceptCodatClientInvitation,
  useDeclineCodatClientInvitation,
  useGetCodatClientInvitation,
} from "api/clientInvites";
import { UserClientInfo, useUserClientQuery } from "api/clients";
import React, { useCallback, useEffect, useState } from "react";
import Cookies from "universal-cookie";

import FlexContainer from "components/FlexContainer";
import Page from "components/Page";
import PageHeader from "components/PageHeader";
import config from "config";

import styles from "./AcceptClientInvite.module.css";

const cookies = new Cookies();

interface AcceptClientInviteProps {
  inviteId: string;
  params: string;
}

export const AcceptClientInvite: React.FC<AcceptClientInviteProps> = ({
  inviteId,
  params,
}) => {
  const { addToast } = useToast();
  const { sendEvent, sendPageViewEvent } = useAnalytics();

  const [clientInfo, setClientInfo] = useState<UserClientInfo>();
  const [clientImages, setClientImages] = useState<ClientImageUrls>();

  const [{ loading: isLoadingAcceptInvite }, acceptCodatClientInvitation] =
    useAcceptCodatClientInvitation({
      clientInvitationId: inviteId,
    });
  const [{ loading: isLoadingDeclineInvite }, declineCodatClientInvitation] =
    useDeclineCodatClientInvitation({
      clientInvitationId: inviteId,
    });

  const onAccept = useCallback(
    (showToast = true) => {
      sendEvent("Accept Client Invitation Button - Click");
      acceptCodatClientInvitation()
        .then((acceptedInv) => {
          if (showToast) {
            addToast({
              message: `User successfully added to client`,
              variant: "success",
            });
          }

          window.sessionStorage.clear();
          cookies.set(
            config.APP_CLIENT_COOKIE_NAME,
            acceptedInv.data.codatClientId,
            {
              domain: ".codat.io",
            }
          );
          window.location.href = `${config.PORTAL_URL}/${params}`;
        })
        .catch(() => {
          addToast({
            message: `User could not accept invitation`,
            variant: "error",
          });
        });
    },
    [acceptCodatClientInvitation, addToast, params, sendEvent]
  );

  const onDecline = () => {
    sendEvent("Decline Client Invitation Button - Click");
    declineCodatClientInvitation()
      .then(() => {
        addToast({
          message: `Invitation declined`,
          variant: "info",
        });
      })
      .catch(() => {
        addToast({
          message: `Invitation cannot be declined`,
          variant: "error",
        });
      });
  };

  const [{ loading, data: invitation }] = useGetCodatClientInvitation({
    clientInvitationId: inviteId,
  });

  const [, getUserClient] = useUserClientQuery({
    clientId: invitation?.codatClientId ?? "UNKNOWN",
  });

  const [{ loading: clientImageLoading }, getClientImages] =
    useClientImageUrlsQuery(invitation?.codatClientId ?? "UNKNOWN");

  useEffect(() => {
    if (invitation && !clientInfo) {
      getUserClient().then((userClientInfo) => {
        setClientInfo(userClientInfo.data);
      });
    }
  }, [invitation, clientInfo, getUserClient]);

  useEffect(() => {
    (async () => {
      try {
        if (invitation && invitation.codatClientId && !clientImages) {
          const images = await getClientImages();
          setClientImages(images.data);
        }
      } catch (e) {
        console.error("Could not get client images", e); // eslint-disable-line no-console
      }
    })();
  }, [invitation, invitation?.codatClientId, clientImages, getClientImages]);

  useEffect(() => {
    if (invitation && !invitation.requiresConsent) {
      onAccept(false);
    }
  }, [invitation, onAccept]);

  useEffect(() => {
    sendPageViewEvent("Accept Client Invite");
  }, [sendPageViewEvent]);

  if (
    loading ||
    clientImageLoading ||
    (invitation && !invitation.requiresConsent)
  ) {
    return (
      <Page>
        <Skeleton />
      </Page>
    );
  }

  if (invitation?.inviteExpiresOn) {
    const expiresOn = Date.parse(invitation?.inviteExpiresOn);
    if (expiresOn < Date.now()) {
      return (
        <>
          <PageHeader
            header={
              "Invite has expired. Please contact your client to send another invite."
            }
          />
        </>
      );
    }
  }

  if (invitation?.inviteAcceptedOn) {
    return (
      <>
        <PageHeader
          header={
            "Invite was accepted on " + new Date(invitation?.inviteAcceptedOn)
          }
        />
      </>
    );
  }

  if (invitation?.inviteDeclinedOn) {
    return (
      <>
        <PageHeader
          header={
            "Invite was declined on " + new Date(invitation?.inviteDeclinedOn)
          }
        />
      </>
    );
  }

  return (
    <>
      <Page>
        <Card className={styles.card}>
          <Typography
            className={styles.header}
            isSkeleton={!clientInfo?.clientName}
            variant="heading_xl"
            as="h2"
            data-testid="client-invite-title"
          >
            {"You've been invited to join\n" + clientInfo?.clientName}
          </Typography>
          <FlexContainer justifyContent="center">
            {clientImages?.logoUrl && (
              <div>
                <img
                  className={styles.logo}
                  src={clientImages?.logoUrl}
                  alt="Company Logo"
                  data-testid="client-logo"
                />
              </div>
            )}
          </FlexContainer>
          <div>
            <Button
              data-testid="client-invite-decline"
              variant="secondary"
              label="Decline"
              onClick={onDecline}
              isLoading={isLoadingDeclineInvite}
            />
            <Button
              data-testid="client-invite-accept"
              variant="primary"
              label="Accept"
              onClick={() => onAccept()}
              isLoading={isLoadingAcceptInvite}
              className={styles.acceptButton}
            />
          </div>
        </Card>
      </Page>
    </>
  );
};
